import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'

import useWindowWidth from '../../../../../hooks/useWindowWidth'
import { compactNumber } from '../../format'

import style from './style.module.css'

// SVG ICONS
import Arrow4Icon from '../../../../../assets/svgs/Arrow4'
import Filter2Icon from '../../../../../assets/svgs/Filter2'
import CheckBox2Icon from '../../../../../assets/svgs/Checkbox2'
import ArrowDown2Icon from '../../../../../assets/svgs/ArrowDown2'
import CompanyIcon from '../../../../../assets/svgs/Company'
import PinIcon from '../../../../../assets/svgs/Pin'
import IndustryIcon from '../../../../../assets/svgs/Industry'
import PeopleIcon from '../../../../../assets/svgs/People'
import Bag2Icon from '../../../../../assets/svgs/Bag2'
import StatisticsIcon from '../../../../../assets/svgs/Statistics'
import CrossSpotlightIcon from '../../../../../assets/svgs/CrossSpotlight'
import SearchIcon from '../../../../../assets/svgs/Search'
import SkillsIcon from '../../../../../assets/svgs/Skills'

/**
 * The multi-select facet groups, in render order. This mirrors FACET_GROUPS in
 * the page container; it is duplicated rather than imported because importing
 * from '../../index' would create a module cycle (the page imports this file).
 *
 * "Organisation type" leads because it is the one the page ships with a value
 * already applied — Spotlight defaults to companies, and this is where a user
 * goes to add universities, municipalities or publishers back in.
 */
const FACET_GROUPS = [
  { key: 'orgKinds', optionsKey: 'orgKinds', label: 'Organisation type', Icon: CompanyIcon },
  { key: 'domainKeys', optionsKey: 'domains', label: 'Industry', Icon: IndustryIcon },
  { key: 'countryCodes', optionsKey: 'countries', label: 'Location', Icon: PinIcon },
  { key: 'sizeTiers', optionsKey: 'sizeTiers', label: 'Company size', Icon: PeopleIcon },
  { key: 'contentTypes', optionsKey: 'contentTypes', label: 'Offers', Icon: Bag2Icon },
  { key: 'roleFamilyKeys', optionsKey: 'roleFamilies', label: 'Hiring for', Icon: SkillsIcon },
]

/** Above this many options a group gets its own inline "Filter…" box. */
const OPTION_SEARCH_THRESHOLD = 12

/** The rail's Statistics rows, read straight off GET /spotlight/stats. */
const STAT_ROWS = [
  { key: 'companies', label: 'companies' },
  { key: 'hiringCompanies', label: 'hiring now' },
  { key: 'openRoles', label: 'open roles' },
  { key: 'upcomingEvents', label: 'upcoming events' },
  { key: 'savedCount', label: 'saved' },
]

const CheckMark = ({ className }) => (
  <svg
    className={className}
    xmlns="http://www.w3.org/2000/svg"
    width="14"
    height="14"
    viewBox="0 0 14 14"
    fill="none"
    aria-hidden="true"
    focusable="false"
  >
    <path
      d="M2.5 7.3L5.4 10.2L11.5 3.8"
      stroke="#61487C"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const Filter = ({
  isFilterOpen,
  setIsFilterOpen,
  options,
  optionsFailed = false,
  filters,
  stats,
  activeFilterCount,
  onToggleFacet,
  onSetFlag,
  onSearch,
  onClearFilters,
  onRetryOptions,
}) => {
  const isMobile = useWindowWidth() <= 768

  // Which accordion groups are expanded, and the per-group "Filter…" text.
  const [expandedGroups, setExpandedGroups] = useState({})
  const [groupQueries, setGroupQueries] = useState({})

  const navRef = useRef(null)
  const searchRef = useRef(null)
  const [navOverflows, setNavOverflows] = useState(false)

  const handleOpen = useCallback(() => {
    setIsFilterOpen((prev) => !prev)
  }, [setIsFilterOpen])

  // A collapsed 46px rail has nowhere to put an option list, so anything that
  // would expand a group opens the rail instead.
  const handleGroupClick = useCallback(
    (groupKey) => {
      if (!isFilterOpen) {
        setIsFilterOpen(true)
        return
      }
      setExpandedGroups((prev) => ({ ...prev, [groupKey]: !prev[groupKey] }))
    },
    [isFilterOpen, setIsFilterOpen]
  )

  const handleSearchButton = useCallback(() => {
    if (!isFilterOpen) {
      setIsFilterOpen(true)
      // Focus once the rail has finished widening.
      window.setTimeout(() => searchRef.current?.focus(), 320)
      return
    }
    searchRef.current?.focus()
  }, [isFilterOpen, setIsFilterOpen])

  const handleClearSearch = useCallback(() => {
    onSearch('')
    searchRef.current?.focus()
  }, [onSearch])

  // Closing the rail resets the accordion so re-opening is a clean slate.
  useEffect(() => {
    if (!isFilterOpen) {
      setExpandedGroups({})
      setGroupQueries({})
    }
  }, [isFilterOpen])

  // "Scroll to see more" is only honest when the nav really does overflow.
  const measureOverflow = useCallback(() => {
    const nav = navRef.current
    if (!nav) return
    setNavOverflows(nav.scrollHeight - nav.clientHeight > 4)
  }, [])

  useLayoutEffect(() => {
    measureOverflow()
    // Everything below is a re-measure trigger rather than a value the effect reads.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [measureOverflow, isFilterOpen, options, stats, filters, expandedGroups, groupQueries, isMobile])

  useEffect(() => {
    window.addEventListener('resize', measureOverflow)
    return () => window.removeEventListener('resize', measureOverflow)
  }, [measureOverflow])

  const renderGroup = (group) => {
    const { key, optionsKey, label, Icon } = group
    const selected = filters[key] || []
    const items = options ? options[optionsKey] || [] : null
    const isExpanded = isFilterOpen && !!expandedGroups[key]
    const listId = `spotlight-facet-${key}`

    const query = (groupQueries[key] || '').trim().toLowerCase()
    const showOptionSearch = !!items && items.length > OPTION_SEARCH_THRESHOLD
    const visible =
      items && query
        ? items.filter((option) => String(option.label || '').toLowerCase().includes(query))
        : items

    return (
      <li className={style.filter__group} key={key}>
        <button
          type="button"
          id={`${listId}-trigger`}
          className={`${style.filter__item} ${selected.length ? style.filter__item_active : ''}`}
          onClick={() => handleGroupClick(key)}
          aria-expanded={isExpanded}
          aria-controls={listId}
          data-log-action="spotlight_filter_group"
        >
          <span className={style.filter__item_label}>
            <Icon />
            <span>{label}</span>
          </span>

          <span className={style.filter__item_right}>
            {selected.length > 0 && (
              <span className={style.filter__item_badge}>{selected.length}</span>
            )}
            <span
              className={`${style.filter__item_chevron} ${
                isExpanded ? style.filter__item_chevron_open : ''
              }`}
            >
              <ArrowDown2Icon />
            </span>
          </span>
        </button>

        {isExpanded && (
          <div className={style.filter__options} id={listId} role="group" aria-labelledby={`${listId}-trigger`}>
            {showOptionSearch && (
              <input
                type="text"
                className={style.filter__optionSearch}
                placeholder="Filter…"
                value={groupQueries[key] || ''}
                onChange={(event) =>
                  setGroupQueries((prev) => ({ ...prev, [key]: event.target.value }))
                }
                aria-label={`Filter ${label} options`}
                data-log-action="spotlight_filter_option_search"
              />
            )}

            {/* Options failed to load. Without a way back, one transient error
                left every facet empty for the rest of the session. */}
            {!items && optionsFailed && (
              <div className={style.filter__optionError}>
                <span>Couldn&apos;t load filters.</span>
                {onRetryOptions && (
                  <button
                    type="button"
                    className={style.filter__optionRetry}
                    onClick={onRetryOptions}
                    data-log-action="spotlight_filter_retry"
                  >
                    Retry
                  </button>
                )}
              </div>
            )}

            {!items && !optionsFailed && (
              <ul className={style.filter__optionList}>
                {[0, 1, 2].map((n) => (
                  <li key={n} className={style.filter__optionGhost} aria-hidden="true" />
                ))}
              </ul>
            )}

            {items && visible.length > 0 && (
              <ul className={style.filter__optionList}>
                {visible.map((option) => {
                  const isOn = selected.includes(option.key)
                  return (
                    <li key={option.key}>
                      <button
                        type="button"
                        role="checkbox"
                        aria-checked={isOn}
                        className={`${style.filter__option} ${
                          isOn ? style.filter__option_selected : ''
                        }`}
                        onClick={() => onToggleFacet(key, option.key)}
                        data-log-action="spotlight_filter_facet"
                      >
                        <span className={style.filter__option_box} aria-hidden="true">
                          {isOn && <CheckMark className={style.filter__option_check} />}
                        </span>
                        <span className={style.filter__option_label}>{option.label}</span>
                        {Number.isFinite(Number(option.count)) && (
                          <span className={style.filter__option_count}>
                            {compactNumber(option.count)}
                          </span>
                        )}
                      </button>
                    </li>
                  )
                })}
              </ul>
            )}

            {items && visible.length === 0 && (
              <p className={style.filter__optionEmpty}>
                {query ? 'No matches' : 'Nothing to filter by yet'}
              </p>
            )}
          </div>
        )}
      </li>
    )
  }

  const renderToggle = ({ name, label }) => {
    const isOn = !!filters[name]
    return (
      <button
        key={name}
        type="button"
        className={`${style.filter__checkSaved} ${isOn ? style.filter__checkSaved_on : ''}`}
        onClick={() => onSetFlag(name, !filters[name])}
        aria-pressed={isOn}
        data-log-action="spotlight_filter_flag"
      >
        <span className={style.filter__checkBox}>
          <span className={style.filter__checkBox_outline}>
            <CheckBox2Icon />
          </span>
          {isOn && <CheckMark className={style.filter__checkMark} />}
        </span>
        <span className={style.filter__checkSaved_text}>{label}</span>
      </button>
    )
  }

  const toggles = [
    { name: 'savedOnly', label: `See saved (${stats?.savedCount ?? 0})` },
    { name: 'hiring', label: 'Hiring now' },
    { name: 'remote', label: 'Remote roles' },
  ]

  return (
    <div className={`${style.filter} ${isFilterOpen ? style.filter_open : ''}`}>
      {isMobile && (
        <button
          type="button"
          className={style.filter__closeButton}
          onClick={handleOpen}
          aria-label="Close filters"
          data-log-action="spotlight_filter_close"
        >
          <CrossSpotlightIcon />
        </button>
      )}

      <button
        type="button"
        className={style.filter__openButton}
        onClick={handleOpen}
        aria-expanded={isFilterOpen}
        aria-label={isFilterOpen ? 'Collapse filters' : 'Expand filters'}
        data-log-action="spotlight_filter_toggle"
      >
        <Arrow4Icon />
      </button>

      <nav className={style.filter__nav} ref={navRef} aria-label="Company filters">
        <div className={style.filter__mainIcon}>
          <button
            type="button"
            className={style.filter__mainIcon_block}
            onClick={() => !isMobile && handleOpen()}
            aria-expanded={isFilterOpen}
            aria-label={isFilterOpen ? 'Collapse filters' : 'Expand filters'}
            data-log-action="spotlight_filter_toggle"
          >
            <Filter2Icon />
            <span>Filter</span>
          </button>
          <div className={style.filter__mainIcon_line}></div>
        </div>

        <div className={style.filter__search}>
          <button
            type="button"
            className={style.filter__search_button}
            onClick={handleSearchButton}
            aria-label="Search companies"
            data-log-action="spotlight_filter_search_focus"
          >
            <SearchIcon />
          </button>

          <input
            ref={searchRef}
            type="text"
            className={style.filter__search_input}
            placeholder="Search companies"
            value={filters.search || ''}
            onChange={(event) => onSearch(event.target.value)}
            aria-label="Search companies"
            tabIndex={isFilterOpen ? 0 : -1}
            data-log-action="spotlight_filter_search"
          />

          {isFilterOpen && !!filters.search && (
            <button
              type="button"
              className={style.filter__search_clear}
              onClick={handleClearSearch}
              aria-label="Clear search"
              data-log-action="spotlight_filter_search_clear"
            >
              <CrossSpotlightIcon />
            </button>
          )}
        </div>

        <div className={style.filter__toggles}>{toggles.map(renderToggle)}</div>

        <ul className={style.filter__list}>{FACET_GROUPS.map(renderGroup)}</ul>

        {activeFilterCount > 0 && (
          <button
            type="button"
            className={style.filter__clearAll}
            onClick={onClearFilters}
            data-log-action="spotlight_filter_clear"
          >
            Clear all ({activeFilterCount})
          </button>
        )}

        <div className={`${style.filter__mainIcon} ${style.statistics}`}>
          {isFilterOpen ? (
            <div className={style.filter__mainIcon_block}>
              <StatisticsIcon />
              <span>Statistics</span>
            </div>
          ) : (
            // Collapsed, the icon is the only affordance it has, so it opens the rail.
            <button
              type="button"
              className={style.filter__mainIcon_block}
              onClick={() => setIsFilterOpen(true)}
              aria-label="Expand filters"
              data-log-action="spotlight_filter_toggle"
            >
              <StatisticsIcon />
              <span>Statistics</span>
            </button>
          )}
          <div className={style.filter__mainIcon_line}></div>
        </div>

        <ul className={style.statistics__list}>
          {stats
            ? STAT_ROWS.map((row) => (
                <li className={style.statistics__item} key={row.key}>
                  <span className={style.statistics__item_number}>
                    {compactNumber(stats[row.key])}
                  </span>
                  <span className={style.statistics__item_text}>{row.label}</span>
                </li>
              ))
            : STAT_ROWS.map((row) => (
                <li
                  className={`${style.statistics__item} ${style.statistics__item_ghost}`}
                  key={row.key}
                  aria-hidden="true"
                />
              ))}
        </ul>
      </nav>

      {navOverflows && <p className={style.nav__scroll_text}>Scroll to see more</p>}

      {isMobile && (
        <button
          type="button"
          className={style.filter__apply}
          onClick={() => setIsFilterOpen(false)}
          data-log-action="spotlight_filter_apply"
        >
          Show results
          {activeFilterCount > 0 && (
            <span className={style.filter__apply_badge}>{activeFilterCount}</span>
          )}
        </button>
      )}
    </div>
  )
}

export default Filter
